/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState } from "react";
import { useApi } from "../api";
import { apiUrl } from "../auth";
import { ArrowLeft, FileText, ChevronDown, Loader2 } from "lucide-react";
import {
  ChecklistSection,
  NEW_CHECKLIST_SECTIONS,
  extraKindFor,
  isExpiryDay,
} from "@ambulance/shared";
import {
  NEW_CHECKLIST_SECTIONS_V1,
  isV1Checklist,
} from "@ambulance/shared";
import { SignatureView } from "../components/SignaturePad";
import { OfficialChecklistForm } from "@ambulance/shared";
import { fmtDateTime } from "@ambulance/shared";

// Print only the official-form layout (portrait A4): inject a print-time
// stylesheet that isolates #official-checklist, then remove it afterwards.
function printOfficial() {
  const style = document.createElement("style");
  // Everything except the official form is hidden AND removed from layout
  // (display:none via :has) so the hidden report can't produce trailing
  // blank pages; the visibility rules remain as a fallback.
  style.textContent = [
    "@media print{",
    "@page{size:A4 portrait;margin:5mm}",
    "html,body{height:auto!important}",
    "body *{visibility:hidden!important}",
    "#official-checklist,#official-checklist *{visibility:visible!important}",
    "#official-checklist{display:block!important;position:absolute;left:0;top:0;width:100%}",
    "body *:not(:has(#official-checklist)):not(#official-checklist):not(#official-checklist *){display:none!important}",
    "}",
  ].join("");
  document.head.appendChild(style);
  const cleanup = () => {
    style.remove();
    window.removeEventListener("afterprint", cleanup);
  };
  window.addEventListener("afterprint", cleanup);
  window.print();
}

// Read-only view (screen + print) for checklists submitted with the REVISED
// form (3-state marks). Old records keep the legacy replica view.

function MarkChip({ v }: { v?: string }) {
  const cls =
    v === "Y"
      ? "bg-emerald-100 text-emerald-700"
      : v === "N"
        ? "bg-red-100 text-red-700"
        : v === "NA"
          ? "bg-slate-200 text-slate-600"
          : "bg-slate-100 text-slate-400";
  const label = v === "Y" ? "✓" : v === "N" ? "✗" : v === "NA" ? "N/A" : "—";
  return (
    <span
      className={`inline-block min-w-[2.25rem] text-center text-xs font-bold px-2 py-0.5 rounded ${cls}`}
    >
      {label}
    </span>
  );
}

// Collapsible section — closed by default; click the header to expand.
function SectionCard({
  title,
  children,
  headerRight,
}: {
  title: string;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden break-inside-avoid">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full bg-slate-50 hover:bg-slate-100 px-3 py-2 flex items-center justify-between gap-2 text-left ${
          open ? "border-b border-slate-200" : ""
        }`}
      >
        <span className="text-sm font-semibold text-slate-800">{title}</span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {headerRight}
          <ChevronDown
            className={`w-4 h-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>
      {open && <div className="divide-y divide-slate-100">{children}</div>}
    </div>
  );
}

export function NewChecklistDetail({
  data,
  onBack,
}: {
  data: any;
  onBack: () => void;
}) {
  const [pdfBusy, setPdfBusy] = useState(false);
  const api = useApi();

  // Generate the PDF on the server so it's identical on every device (matches
  // the desktop layout). Falls back to the browser print dialog on failure.
  async function downloadPdf() {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const res = await fetch(`${apiUrl}/api/v1/checklists/${data.id}/pdf`, {
        method: "POST",
        headers: { Authorization: `Bearer ${api.token}` },
      });
      if (!res.ok) throw new Error("PDF request failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Ambulance Checklist ${data.ambulanceNo ?? ""} ${data.date ?? ""}`.trim() + ".pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      printOfficial();
    } finally {
      setPdfBusy(false);
    }
  }

  const photos = (data.images ?? []).filter(
    (img: any) => img.label !== "Defib Test Strip"
  );
  const defibStrip = (data.images ?? []).find(
    (img: any) => img.label === "Defib Test Strip"
  );
  const ho = (data.handover ?? {}) as Record<string, string>;
  // Show the record against the item list of the revision it was submitted
  // under, so an old checklist reads as the document that was signed.
  const v1 = isV1Checklist(data);
  const sections: ReadonlyArray<ChecklistSection> = v1
    ? NEW_CHECKLIST_SECTIONS_V1
    : NEW_CHECKLIST_SECTIONS;
  // Monthly checklists collected expiry dates in the per-item column; daily
  // ones used it for remarks. The v1 form always collected expiry dates.
  const expiryDay = v1 || isExpiryDay(data.date);

  const info: [string, string][] = [
    ["Date", data.date],
    ["Station", data.hospitalName],
    ["Ambulance Code", data.ambulanceNo],
    ["Shift", data.shift === "D" ? "Day" : "Night"],
    ["Type", data.type],
    ["Level", data.level],
    ["Mileage (KM)", data.mileage || "—"],
    ["Submitted", fmtDateTime(data.submittedAt)],
    ["Submitted By", data.submittedByName || "—"],
  ];

  return (
    <div className="force-print-colors doc-print max-w-5xl mx-auto px-2 pb-8">
      <div className="no-print flex items-center justify-between mb-4 gap-2">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={downloadPdf}
          disabled={pdfBusy}
          className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg bg-[var(--hmg-red)] text-white hover:bg-[var(--hmg-red-dark)] text-sm font-semibold disabled:opacity-60"
        >
          {pdfBusy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <FileText className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">
            {pdfBusy ? "Preparing PDF…" : "Download PDF"}
          </span>
          <span className="sm:hidden">{pdfBusy ? "…" : "PDF"}</span>
        </button>
      </div>

      <div id="official-checklist">
        <OfficialChecklistForm d={data} />
      </div>

      <div className="flex items-center gap-3 sm:gap-4 border-b-2 border-[var(--hmg-red)] pb-3 mb-4">
        <img
          src="/hmg-logo.png"
          alt="Dr Sulaiman Al Habib Medical Group"
          width={240}
          height={62}
          className="h-11 sm:h-14 w-auto flex-shrink-0"
        />
        <div className="border-l border-slate-300 pl-3 sm:pl-4">
          <div className="text-[10px] sm:text-xs uppercase tracking-wider text-[var(--hmg-red)] font-bold">
            Emergency Medical Services
          </div>
          <div className="text-base sm:text-xl font-bold text-slate-900">
            Ambulance Daily Checklist
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 break-inside-avoid">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {info.map(([k, v]) => (
            <div key={k}>
              <div className="text-xs uppercase tracking-wide text-slate-400">
                {k}
              </div>
              <div className="text-sm font-medium text-slate-800">{v}</div>
            </div>
          ))}
        </div>
        {data.externalNote?.trim() && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="text-xs uppercase tracking-wide text-slate-400">
              External / Additional Note
            </div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap">
              {data.externalNote}
            </div>
          </div>
        )}
      </div>

      {photos.length > 0 && (
        <div className="mb-4">
          <SectionCard title="Ambulance Photos">
            <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {photos.map((img: any) => (
                <figure key={img.storageId}>
                  {img.url && (
                    <img
                      src={img.url}
                      alt={img.label}
                      className="w-full h-40 object-cover rounded-lg border border-slate-200"
                    />
                  )}
                  <figcaption className="text-xs text-slate-500 mt-1">
                    {img.label}
                  </figcaption>
                </figure>
              ))}
            </div>
          </SectionCard>
        </div>
      )}

      <div className="space-y-4">
        {sections.map((sec) => {
          const markRec = (data[sec.key] ?? {}) as Record<string, string>;
          const extraRec = (data[sec.extraKey] ?? {}) as Record<string, string>;
          return (
            <SectionCard
              key={sec.key}
              title={sec.label}
              headerRight={
                sec.key === "aclsItems" && data.aclsSealNumber ? (
                  <span className="text-xs text-slate-600">
                    Seal No:{" "}
                    <span className="font-semibold">{data.aclsSealNumber}</span>
                  </span>
                ) : undefined
              }
            >
              {sec.items.map((item) => {
                const [k, label, qty] = item as unknown as [
                  string,
                  string,
                  string?,
                ];
                const extra = (extraRec[k] ?? "").trim();
                return (
                  <div
                    key={k}
                    className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm"
                  >
                    <span className="text-slate-700 flex-1 min-w-0">
                      {label}
                      {qty && (
                        <span className="text-xs text-slate-400"> (×{qty})</span>
                      )}
                      {extra && (
                        <span className="block text-xs text-slate-500">
                          {extraKindFor(sec.extra, expiryDay) === "expiry"
                            ? "Expiry: "
                            : "Remark: "}
                          {extra}
                        </span>
                      )}
                    </span>
                    <MarkChip v={markRec[k]} />
                  </div>
                );
              })}
            </SectionCard>
          );
        })}

        {defibStrip && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 break-inside-avoid">
            <div className="text-sm font-semibold text-slate-800 mb-3">
              Defibrillator Test Strip
            </div>
            {defibStrip.url && (
              <img
                src={defibStrip.url}
                alt="Defib Test Strip"
                className="max-h-72 rounded-lg border border-slate-200"
              />
            )}
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 p-4 break-inside-avoid">
          <div className="text-sm font-semibold text-slate-800 mb-3">
            Shift Handover
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(
              [
                ["Outgoing Medic", "outgoingName", "outgoingId", "outgoingSignature"],
                ["Incoming Medic", "incomingName", "incomingId", "incomingSignature"],
              ] as const
            ).map(([title, nameK, idK, sigK]) => (
              <div key={title} className="rounded-lg border border-slate-200 p-3">
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
                  {title}
                </div>
                <div className="text-sm font-medium text-slate-800">
                  {ho[nameK] || "—"}
                  {ho[idK] ? (
                    <span className="text-xs text-slate-500"> (ID: {ho[idK]})</span>
                  ) : null}
                </div>
                <div className="mt-2">
                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
                    Signature
                  </div>
                  <SignatureView value={ho[sigK]} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-[11px] text-slate-400">
          Marking guide: ✓ = Available and not expired · ✗ = Not available.
        </p>
      </div>
    </div>
  );
}
